/**
 * src/services/sfuClient.ts
 *
 * mediasoup-client wrapper that terminates WebRTC media at a central
 * SFU (see `server/ws/sfu.js`) instead of opening a per-viewer peer
 * connection mesh.  Exposes the same `BroadcastController` /
 * `ViewerController` surface as the P2P path in
 * `webrtcBroadcastService.ts` so the caller doesn't care which
 * transport model is in play.
 *
 * Why an SFU:
 *   - Tesla's in-vehicle network and some 5G carriers block direct
 *     UDP end-to-end, forcing P2P viewers into a black-screen.  Every
 *     media packet here traverses a publicly-reachable UDP/TCP range
 *     (40000-49999) on the SFU VM, which Azure's NSG exposes on the
 *     open internet — so the "nothing reaches me" failure mode in a
 *     mesh is impossible.
 *   - Broadcaster upstream scales O(1) (one encode, one send) rather
 *     than O(viewers) as in the P2P mesh — the SFU fans out.
 *   - Codec negotiation is authoritative at the router: H.264 is
 *     listed first in mediaCodecs on the server so all consumers get
 *     an H.264 pipeline regardless of the broadcaster's browser.
 *
 * Wire protocol (mirrors server/ws/webrtcHub.js handlers):
 *   client → server                         server → client
 *   sfu-get-caps                            sfu-router-caps { rtpCapabilities }
 *   sfu-create-transport { direction }      sfu-transport-created { direction, id, iceParameters, iceCandidates, dtlsParameters }
 *   sfu-connect-transport { transportId,    sfu-transport-connected { transportId }
 *     dtlsParameters }
 *   sfu-produce { transportId, kind,        sfu-produced { producerId, kind }
 *     rtpParameters }
 *   sfu-consume { producerId,               sfu-consumed { id, producerId, kind,
 *     rtpCapabilities }                       rtpParameters, producerPaused }
 *   sfu-resume-consumer { consumerId }      sfu-consumer-resumed { consumerId }
 *   sfu-list-producers                      sfu-producers { producers: [{producerId, kind}] }
 *   (unsolicited)                           sfu-new-producer { producerId, kind }
 *   (unsolicited on error)                  sfu-error { op, reason }
 */

import { Device } from 'mediasoup-client';
import type { types as mstypes } from 'mediasoup-client';

import type {
  BroadcasterOpts,
  BroadcastController,
  ViewerOpts,
  ViewerController,
  ViewerDiagnostics,
} from './webrtcBroadcastService';
import { getIceServers, normaliseCaptionEvent } from './webrtcBroadcastService';

/**
 * How long to wait for a single sfu-* request/response round-trip
 * before rejecting.  The SFU is on one VM in one region, so typical
 * RTTs are <200ms — 10s is a generous upper bound that still lets
 * the UI render a helpful error instead of hanging.
 */
const SFU_RPC_TIMEOUT_MS = 10_000;

/**
 * Viewer-side ICE watchdog timeout — aligned with the P2P path so
 * the UI's retry prompts fire at the same wall-clock for both
 * transports.
 */
const SFU_VIEWER_ICE_TIMEOUT_MS = 20_000;

function resolveBackendDefault(): string {
  // eslint-disable-next-line no-undef
  return (typeof process !== 'undefined' && process.env.REACT_APP_BACKEND_URL) || '';
}

/**
 * Resolve the WebSocket URL to use for SFU signaling.
 *
 * Priority:
 *   1. `opts.backendUrl` (explicit per-call override)
 *   2. `REACT_APP_SFU_WS_URL` (explicit SFU endpoint, e.g. the VM's URL)
 *   3. `REACT_APP_BACKEND_URL` (existing main-backend override)
 *   4. `window.location.origin` (same-origin fallback)
 */
export function resolveSfuWsUrl(backendUrlOverride?: string): string {
  if (backendUrlOverride) {
    return backendUrlOverride.replace(/^http/, 'ws').replace(/\/$/, '') + '/ws/webrtc';
  }
  // Direct `process.env.REACT_APP_SFU_WS_URL` access — see note in
  // webrtcBroadcastService.ts::isSfuEnabled for why indirection
  // breaks CRA's compile-time env substitution.
  // eslint-disable-next-line no-undef
  const sfuUrl: string = (typeof process !== 'undefined' && process.env.REACT_APP_SFU_WS_URL) || '';
  if (sfuUrl) {
    // REACT_APP_SFU_WS_URL may already include the path, or just be a
    // host origin — normalize both forms so ops doesn't have to remember
    // which format the env expects.
    if (/\/ws\/webrtc$/.test(sfuUrl)) return sfuUrl;
    return sfuUrl.replace(/\/$/, '') + '/ws/webrtc';
  }
  const fallback = resolveBackendDefault() ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  if (!fallback) return '';
  return fallback.replace(/^http/, 'ws').replace(/\/$/, '') + '/ws/webrtc';
}

/**
 * Await a single WebSocket message that matches `predicate`.  Shared
 * subscribers (e.g. denied / session_ended handlers) remain wired up
 * on the ws because `removeEventListener` only removes our own
 * one-shot listener.
 */
function waitFor<T = any>(ws: WebSocket, predicate: (msg: any) => boolean, timeoutMs = SFU_RPC_TIMEOUT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const onMsg = (ev: MessageEvent) => {
      let m: any;
      try { m = JSON.parse(String(ev.data)); } catch { return; }
      if (predicate(m)) { cleanup(); resolve(m as T); }
      // Surface any sfu-error for the current op so the caller's
      // .catch gets a useful reason string instead of a 10s stall.
      if (m?.type === 'sfu-error') {
        cleanup();
        reject(new Error(`sfu_error:${m.op || 'unknown'}:${m.reason || 'unknown'}`));
      }
    };
    const onClose = () => { cleanup(); reject(new Error('sfu_ws_closed')); };
    const timer = setTimeout(() => { cleanup(); reject(new Error('sfu_timeout')); }, timeoutMs);
    function cleanup() {
      clearTimeout(timer);
      ws.removeEventListener('message', onMsg);
      ws.removeEventListener('close', onClose);
    }
    ws.addEventListener('message', onMsg);
    ws.addEventListener('close', onClose);
  });
}

function wsSend(ws: WebSocket, payload: object): void {
  if (ws.readyState !== WebSocket.OPEN) return;
  try { ws.send(JSON.stringify(payload)); } catch { /* ignore */ }
}

/**
 * Empty placeholder for the controller returned when the SFU
 * WebSocket URL is not configured.  Keeps the `no_ws_url` error
 * path behaviour identical to the P2P service.
 */
function noopBroadcastController(broadcasterId: string): BroadcastController {
  return {
    broadcasterId,
    stop() {},
    viewerCount() { return 0; },
    getStats() {
      return {
        transportState: 'closed' as RTCIceConnectionState,
        hasVideoProducer: false,
        hasAudioProducer: false,
        videoBytesSent: 0,
        audioBytesSent: 0,
        videoPacketsSent: 0,
        audioPacketsSent: 0,
        ageMs: 0,
      };
    },
    replaceTrack() { return false; },
    setPaused() { return false; },
    sendGift() { return false; },
  };
}

function noopViewerController(broadcasterId: string): ViewerController {
  return { broadcasterId, stop() {}, sendGift() { return false; } };
}

/* -----------------------------------------------------------------
 * Broadcaster
 * -------------------------------------------------------------- */

/**
 * Compute the next reconnect delay with capped exponential backoff.
 * Pattern: 2s, 4s, 8s, 15s, 15s, …  Kept short on the first attempt so
 * a Wi-Fi roaming event or Tesla in-vehicle network blip is covered in
 * well under 10s — within the server's 60s broadcaster grace window.
 */
function reconnectDelayMs(attempt: number): number {
  const base = Math.min(15_000, 2_000 * Math.pow(2, Math.max(0, attempt - 1)));
  return Math.max(1_500, base);
}

export function startBroadcastSfu(stream: MediaStream, opts: BroadcasterOpts): BroadcastController {
  const url = resolveSfuWsUrl(opts.backendUrl);
  if (!url) {
    opts.onError?.(new Error('no_ws_url'));
    return noopBroadcastController(opts.broadcasterId);
  }

  // Lifecycle flags:
  //   - `stopped`   : caller asked us to terminate (stop()) OR the
  //                   server sent a terminal message (denied /
  //                   session_ended).  No further reconnects.
  //   - `connected` : set once the *first* full setup (transport +
  //                   produce) completes.  Used to distinguish a
  //                   "connect attempt failed entirely" from a
  //                   "mid-session drop" and to know whether to fire
  //                   `onReconnected` on subsequent setups.
  let stopped = false;
  let connected = false;
  // Tracks the user-requested pause state across reconnects.  We
  // re-apply this to freshly-created producers after every
  // successful `setupBroadcaster` call so a transient signaling
  // drop mid-pause never silently "resumes" the stream.
  let pausedState = false;
  let ws: WebSocket | null = null;
  let device: Device | null = null;
  let sendTransport: mstypes.Transport | null = null;
  let producers = new Map<'video' | 'audio', mstypes.Producer>();
  let currentViewerCount = 0;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // Caller owns `stream` — we never stop tracks on our side.  These
  // refs stay stable across reconnects; re-produce uses whichever
  // track is current (covers the camera-flip case where replaceTrack
  // was called since the last reconnect).
  const currentTracks: { video: MediaStreamTrack | null; audio: MediaStreamTrack | null } = {
    video: stream.getVideoTracks()[0] || null,
    audio: stream.getAudioTracks()[0] || null,
  };

  // Clean up the mediasoup handles bound to the *current* ws.  Called
  // between reconnect attempts and on final stop.  Producers/transport
  // are re-created on the next setupBroadcaster run.
  const teardownWsState = () => {
    producers.forEach((p) => { try { p.close(); } catch { /* ignore */ } });
    producers = new Map();
    try { sendTransport?.close(); } catch { /* ignore */ }
    sendTransport = null;
    device = null;
  };

  const setupBroadcaster = async (currentWs: WebSocket) => {
    wsSend(currentWs, { type: 'sfu-get-caps' });
    const caps = await waitFor<{ type: string; rtpCapabilities: mstypes.RtpCapabilities }>(
      currentWs, (m) => m?.type === 'sfu-router-caps');

    device = new Device();
    await device.load({ routerRtpCapabilities: caps.rtpCapabilities });

    wsSend(currentWs, { type: 'sfu-create-transport', direction: 'send' });
    const tParams = await waitFor<{
      type: string; direction: 'send' | 'recv';
      id: string;
      iceParameters: mstypes.IceParameters;
      iceCandidates: mstypes.IceCandidate[];
      dtlsParameters: mstypes.DtlsParameters;
    }>(currentWs, (m) => m?.type === 'sfu-transport-created' && m?.direction === 'send');

    sendTransport = device.createSendTransport({
      id: tParams.id,
      iceParameters: tParams.iceParameters,
      iceCandidates: tParams.iceCandidates,
      dtlsParameters: tParams.dtlsParameters,
      iceServers: getIceServers(),
    });

    sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      wsSend(currentWs, {
        type: 'sfu-connect-transport',
        transportId: sendTransport!.id,
        dtlsParameters,
      });
      waitFor(currentWs, (m) => m?.type === 'sfu-transport-connected' && m?.transportId === sendTransport!.id)
        .then(() => callback())
        .catch((err) => errback(err as Error));
    });

    sendTransport.on('produce', ({ kind, rtpParameters }, callback, errback) => {
      wsSend(currentWs, {
        type: 'sfu-produce',
        transportId: sendTransport!.id,
        kind,
        rtpParameters,
      });
      waitFor<{ type: string; producerId: string; kind: 'audio' | 'video' }>(
        currentWs, (m) => m?.type === 'sfu-produced' && m?.kind === kind)
        .then((m) => callback({ id: m.producerId }))
        .catch((err) => errback(err as Error));
    });

    sendTransport.on('connectionstatechange', (state) => {
      if (stopped) return;
      // Mid-session transport failure: close our ws so the reconnect
      // loop kicks in.  We don't surface `onError` here — the UI
      // should react via `onReconnecting` from the ws close path.
      if (state === 'failed' || state === 'closed') {
        try { currentWs.close(); } catch { /* ignore */ }
      }
    });

    if (currentTracks.video) {
      const producer = await sendTransport.produce({ track: currentTracks.video });
      producers.set('video', producer);
    }
    if (currentTracks.audio) {
      const producer = await sendTransport.produce({ track: currentTracks.audio });
      producers.set('audio', producer);
    }
    // Re-apply the user-requested pause state if we were paused
    // before a reconnect.  mediasoup producers start resumed by
    // default; without this step, every ws drop would silently
    // un-pause the stream on the viewer side.
    if (pausedState) {
      try { producers.get('video')?.pause(); } catch { /* ignore */ }
      try { producers.get('audio')?.pause(); } catch { /* ignore */ }
      try { currentWs.send(JSON.stringify({ type: 'broadcast_paused' })); } catch { /* ignore */ }
    }
  };

  const connect = () => {
    if (stopped) return;
    attempt += 1;
    const w = new WebSocket(url);
    ws = w;

    w.addEventListener('open', () => {
      if (stopped || ws !== w) return;
      wsSend(w, {
        type: 'hello', role: 'broadcaster', broadcasterId: opts.broadcasterId,
        wallet: opts.wallet, country: opts.country,
        liveSessionId: opts.liveSessionId, grantToken: opts.grantToken,
      });
      setupBroadcaster(w).then(() => {
        if (stopped || ws !== w) return;
        const wasReconnect = connected;
        connected = true;
        if (wasReconnect) {
          opts.onReconnected?.();
        }
      }).catch((err) => {
        if (stopped || ws !== w) return;
        // Treat a failed setup as a transient drop — close the ws so
        // the reconnect loop retries via exponential backoff.  If
        // shouldResume returns false, the close handler surfaces the
        // original error to the caller and stops.
        try { w.close(); } catch { /* ignore */ }
      });
    });

    w.addEventListener('message', (ev) => {
      let msg: any;
      try { msg = JSON.parse(String(ev.data)); } catch { return; }
      if (!msg?.type) return;
      switch (msg.type) {
        case 'denied':
          // Any deny during a live session (including reconnect
          // rejects like `reconnect_rejected` / `session_not_live`)
          // is terminal.  Flip `stopped` so the close handler
          // doesn't auto-retry.
          stopped = true;
          opts.onDenied?.(msg.reason || 'denied');
          try { w.close(); } catch {}
          break;
        case 'session_ended':
          stopped = true;
          opts.onSessionEnded?.(msg.reason || 'ended');
          try { w.close(); } catch {}
          break;
        case 'session_extended':
          if (typeof msg.scheduledEndAt === 'string') {
            opts.onSessionExtended?.({
              scheduledEndAt: msg.scheduledEndAt,
              extensionSeconds: msg.extensionSeconds,
              extensionCount: msg.extensionCount,
              fromViewer: msg.fromViewer,
            });
          }
          break;
        case 'viewers_update':
          if (typeof msg.count === 'number') {
            currentViewerCount = msg.count;
            opts.onViewersUpdate?.(msg.count);
          }
          break;
        case 'joined':
          if (typeof msg.peerId === 'string') {
            opts.onViewerJoined?.({
              peerId: msg.peerId,
              wallet: typeof msg.wallet === 'string' ? msg.wallet : undefined,
              country: typeof msg.country === 'number' ? msg.country : null,
            });
          }
          break;
        case 'left':
          if (typeof msg.peerId === 'string') opts.onViewerLeft?.(msg.peerId);
          break;
        case 'live_gift':
          try {
            opts.onGift?.({
              fromPeerId: String(msg.fromPeerId || ''),
              fromWallet: typeof msg.fromWallet === 'string' ? msg.fromWallet : null,
              assetCode: typeof msg.assetCode === 'string' ? msg.assetCode : 'XLM',
              assetIssuer: typeof msg.assetIssuer === 'string' ? msg.assetIssuer : null,
              amount: String(msg.amount || ''),
              memo: typeof msg.memo === 'string' ? msg.memo : '',
              txHash: typeof msg.txHash === 'string' ? msg.txHash : null,
              sentAt: typeof msg.sentAt === 'number' ? msg.sentAt : Date.now(),
            });
          } catch { /* ignore */ }
          break;
        case 'live_tool_event':
          try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const liveTools = require('./liveToolsService') as typeof import('./liveToolsService');
            const ev = {
              event: msg.event,
              sessionId: msg.sessionId,
              toolId: msg.toolId ?? null,
              tool: msg.tool ?? null,
              snapshot: msg.snapshot,
              error: msg.error,
            } as import('./liveToolsService').LiveToolEvent;
            (opts as any).onLiveToolEvent?.(ev);
            liveTools.dispatchLiveToolEvent(ev);
          } catch { /* ignore */ }
          break;
        case 'live_agent_caption':
        case 'spatial_object_caption':
          try {
            (opts as any).onAgentCaption?.(normaliseCaptionEvent(msg));
          } catch { /* ignore */ }
          break;
        case 'spatial_object_caption_cancel':
          try {
            (opts as any).onCaptionCancel?.({
              spatialObjectId: String(msg.spatial_object_id || ''),
              utteranceId: msg.utterance_id ? String(msg.utterance_id) : null,
              reason: String(msg.reason || 'cancelled'),
            });
          } catch { /* ignore */ }
          break;
        default: break;
      }
    });

    w.addEventListener('error', () => {
      if (stopped || ws !== w) return;
      // An `error` event is always followed by `close` per the
      // WebSocket spec — the close handler below owns the reconnect
      // decision.  We only log here.
    });

    w.addEventListener('close', () => {
      if (ws !== w) return;
      ws = null;
      teardownWsState();

      if (stopped) return;

      const wantsReconnect = typeof opts.shouldResume === 'function'
        ? !!opts.shouldResume()
        : false;
      if (!wantsReconnect) {
        opts.onSessionEnded?.('socket_closed');
        return;
      }
      const delay = reconnectDelayMs(attempt);
      opts.onReconnecting?.(attempt + 1);
      reconnectTimer = setTimeout(connect, delay);
    });
  };

  // Cached counters for `getStats()`. The latest poll wins; we
  // refresh asynchronously from the producer's RTP sender stats so
  // the controller's `getStats()` is fast/sync. Without this every
  // poll would have to await `producer.getStats()` (a Promise), which
  // would force the React badge to be effect-driven.
  //
  // Browser quirks: some browsers (notably Chrome on a fresh session)
  // emit RTP through a producer whose RTCStatsReport never reports
  // `bytesSent` on its `outbound-rtp` entry — `packetsSent` shows up
  // first, then `bytesSent` populates a couple of seconds later. We
  // therefore extract bytes from *any* report that carries a numeric
  // `bytesSent`, AND independently track `packetsSent` so the UI can
  // show a healthy "transport up + RTP flowing" state even on the
  // browsers where the byte counter lags.
  const startedAt = Date.now();
  let lastVideoBytes   = 0;
  let lastAudioBytes   = 0;
  let lastVideoPackets = 0;
  let lastAudioPackets = 0;
  let statsPollTimer: ReturnType<typeof setInterval> | null = null;
  const collect = (stats: RTCStatsReport, kind: 'video' | 'audio') => {
    let bytes = 0;
    let packets = 0;
    stats.forEach((s: any) => {
      if (typeof s?.bytesSent === 'number')   bytes   = Math.max(bytes,   s.bytesSent);
      if (typeof s?.packetsSent === 'number') packets = Math.max(packets, s.packetsSent);
    });
    if (kind === 'video') {
      lastVideoBytes   = Math.max(lastVideoBytes,   bytes);
      lastVideoPackets = Math.max(lastVideoPackets, packets);
    } else {
      lastAudioBytes   = Math.max(lastAudioBytes,   bytes);
      lastAudioPackets = Math.max(lastAudioPackets, packets);
    }
  };
  const refreshBytes = async () => {
    const v = producers.get('video');
    const a = producers.get('audio');
    try { if (v) collect(await v.getStats(), 'video'); }
    catch { /* mediasoup-client occasionally rejects mid-teardown */ }
    try { if (a) collect(await a.getStats(), 'audio'); }
    catch { /* see above */ }
  };
  statsPollTimer = setInterval(() => { void refreshBytes(); }, 1000);

  const transportStateNow = (): RTCIceConnectionState => {
    const raw = (sendTransport as any)?.connectionState;
    switch (raw) {
      case 'connected':    return 'connected';
      case 'connecting':   return 'checking';
      case 'failed':       return 'failed';
      case 'disconnected': return 'disconnected';
      case 'closed':       return 'closed';
      default:             return 'new';
    }
  };

  connect();

  return {
    broadcasterId: opts.broadcasterId,
    viewerCount: () => currentViewerCount,
    getStats: () => ({
      transportState:    transportStateNow(),
      hasVideoProducer:  producers.has('video'),
      hasAudioProducer:  producers.has('audio'),
      videoBytesSent:    lastVideoBytes,
      audioBytesSent:    lastAudioBytes,
      videoPacketsSent:  lastVideoPackets,
      audioPacketsSent:  lastAudioPackets,
      ageMs:             Date.now() - startedAt,
    }),
    replaceTrack(kind, newTrack) {
      currentTracks[kind] = newTrack;
      const producer = producers.get(kind);
      if (!producer) {
        // No live producer right now (either first connect hasn't
        // completed or we're between reconnect attempts).  The next
        // successful setupBroadcaster call will produce the new
        // track from `currentTracks`, so the replacement is still
        // picked up — just not instantly.
        return false;
      }
      producer.replaceTrack({ track: newTrack }).catch((err) => {
      });
      return true;
    },
    stop() {
      stopped = true;
      if (reconnectTimer) { try { clearTimeout(reconnectTimer); } catch {} reconnectTimer = null; }
      if (statsPollTimer) { try { clearInterval(statsPollTimer); } catch {} statsPollTimer = null; }
      // Tell the server this is voluntary so it skips the grace
      // window and ends the paid session immediately.
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'leave' })); } catch { /* ignore */ }
      }
      teardownWsState();
      try { ws?.close(); } catch { /* ignore */ }
      ws = null;
    },
    setPaused(paused: boolean) {
      // SFU path: prefer producer.pause() / resume() which tells
      // mediasoup to drop inbound RTP until we resume — this is
      // more bandwidth-efficient than flipping track.enabled
      // (which would keep the codec producing black frames over
      // the network).  If the producer isn't established yet
      // (mid-reconnect), fall back to track.enabled so the UI
      // state isn't silently lost.
      pausedState = paused;
      const video = producers.get('video');
      const audio = producers.get('audio');
      if (!video && !audio) {
        const vT = currentTracks.video;
        const aT = currentTracks.audio;
        if (!vT && !aT) return false;
        try { if (vT) vT.enabled = !paused; } catch { /* ignore */ }
        try { if (aT) aT.enabled = !paused; } catch { /* ignore */ }
      } else {
        try {
          if (video) {
            if (paused) video.pause(); else video.resume();
          }
        } catch { /* ignore */ }
        try {
          if (audio) {
            if (paused) audio.pause(); else audio.resume();
          }
        } catch { /* ignore */ }
      }
      // Tell the hub so viewers get the overlay.  Falls through
      // silently if the ws is between reconnect attempts — the
      // next resume on the other end will include fresh state.
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({
            type: paused ? 'broadcast_paused' : 'broadcast_resumed',
          }));
        } catch { /* ignore */ }
      }
      return true;
    },
    sendGift(gift) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      try {
        ws.send(JSON.stringify({
          type: 'live_gift',
          assetCode: gift.assetCode,
          assetIssuer: gift.assetIssuer || null,
          amount: gift.amount,
          memo: gift.memo || '',
          txHash: gift.txHash || null,
        }));
        return true;
      } catch {
        return false;
      }
    },
  };
}

/* -----------------------------------------------------------------
 * Viewer
 * -------------------------------------------------------------- */

export function joinBroadcastSfu(opts: ViewerOpts): ViewerController {
  const url = resolveSfuWsUrl(opts.backendUrl);
  if (!url) {
    opts.onError?.(new Error('no_ws_url'));
    return noopViewerController(opts.broadcasterId);
  }

  let stopped = false;
  let connected = false;
  let ws: WebSocket | null = null;
  let device: Device | null = null;
  let recvTransport: mstypes.Transport | null = null;
  let consumers = new Map<string, mstypes.Consumer>();
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Aggregate stream is stable across reconnects — the <video>
  // element remains attached to it while tracks come and go.  New
  // tracks from a reconnected broadcaster are added to this same
  // instance so the UI doesn't flash or remount.
  const aggregateStream = new MediaStream();
  let deliveredStream = false;
  // Broadcaster's declared mode, populated once the server's
  // `welcome` arrives (and possibly refreshed by the
  // `sfu-producers` response on a late join). Default 'media' so
  // legacy browser broadcasts behave exactly as before.
  //
  // 'signaling_only' = headless MCP / geoai broadcaster: no SFU
  // send transport, no producers, ever. The viewer skips
  // recv-transport setup, cancels its ICE watchdog (otherwise it
  // would fire ice_timeout 30s after join even though nothing is
  // actually wrong), and lets the UI render an avatar+captions
  // canvas locally driven by `live_agent_caption` events.
  let broadcasterMode: 'media' | 'signaling_only' = 'media';
  let modeNotified = false;
  const notifyMode = (next: 'media' | 'signaling_only') => {
    if (next === broadcasterMode && modeNotified) return;
    broadcasterMode = next;
    modeNotified = true;
    try { (opts as any).onMode?.(next); } catch { /* never let UI throws kill the WS */ }
  };

  const startedAt = Date.now();
  let iceReached = false;
  const localCandidateTypes = new Set<ViewerDiagnostics['localCandidateTypes'][number]>();
  const remoteCandidateTypes = new Set<ViewerDiagnostics['remoteCandidateTypes'][number]>();

  const wsStateString = (): ViewerDiagnostics['wsState'] => {
    if (!ws) return 'closed';
    switch (ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN:       return 'open';
      case WebSocket.CLOSING:    return 'closing';
      case WebSocket.CLOSED:     return 'closed';
      default:                   return 'unknown';
    }
  };

  // mediasoup-client transports expose `connectionState`
  // ('new' | 'connecting' | 'connected' | 'failed' | 'disconnected' |
  // 'closed') which maps almost 1:1 onto both `iceConnectionState`
  // and `pcState`.  We emit the same value for both so the UI's
  // ViewerDiagnostics panel can render without conditional SFU/P2P
  // branching.
  const mapTransportState = (state: string): RTCIceConnectionState => {
    switch (state) {
      case 'connected':    return 'connected';
      case 'connecting':   return 'checking';
      case 'failed':       return 'failed';
      case 'disconnected': return 'disconnected';
      case 'closed':       return 'closed';
      default:             return 'new';
    }
  };

  const emitDiag = () => {
    if (!opts.onDiagnostics) return;
    const rawState = (recvTransport as any)?.connectionState || 'new';
    const iceState = mapTransportState(rawState);
    try {
      opts.onDiagnostics({
        wsState: wsStateString(),
        iceState,
        pcState: iceState as RTCPeerConnectionState,
        iceGatheringState: (iceReached ? 'complete' : 'gathering') as RTCIceGatheringState,
        localCandidateTypes:  Array.from(localCandidateTypes),
        remoteCandidateTypes: Array.from(remoteCandidateTypes),
        iceTransportPolicy: opts.iceTransportPolicy || 'all',
        elapsedMs: Date.now() - startedAt,
      });
    } catch { /* diagnostics must never crash the pipeline */ }
  };

  const classifyCandidate = (cand: string | null | undefined):
    ViewerDiagnostics['localCandidateTypes'][number] | null => {
    if (!cand) return null;
    const typ = /typ\s+(host|srflx|prflx|relay)/.exec(cand)?.[1];
    if (typ === 'host' && /\.local\s+/.test(cand)) return 'mdns';
    if (typ === 'host' || typ === 'srflx' || typ === 'prflx' || typ === 'relay') return typ;
    return null;
  };

  /**
   * Fetch the current producer list and create a consumer for each
   * one that isn't already being consumed.  Called both on initial
   * join, whenever the server announces a new producer
   * (`sfu-new-producer`), and on every post-reconnect setupViewer.
   */
  const closeConsumersForProducer = (producerId: string) => {
    for (const c of Array.from(consumers.values())) {
      if (c.producerId !== producerId) continue;
      try { aggregateStream.removeTrack(c.track); } catch { /* ignore */ }
      try { c.close(); } catch { /* ignore */ }
      consumers.delete(c.id);
    }
  };

  /** Drop every *video* consumer so an ingest track can become the sole video the <video> element decodes. */
  const detachAllVideoConsumers = () => {
    for (const c of Array.from(consumers.values())) {
      if (c.kind !== 'video') continue;
      try { aggregateStream.removeTrack(c.track); } catch { /* ignore */ }
      try { c.close(); } catch { /* ignore */ }
      consumers.delete(c.id);
    }
  };

  const consumeProducer = async (currentWs: WebSocket, producerId: string, kind: 'audio' | 'video') => {
    if (!device || !recvTransport) return;
    for (const c of Array.from(consumers.values())) {
      if (c.producerId === producerId) return;
    }
    wsSend(currentWs, {
      type: 'sfu-consume', producerId,
      rtpCapabilities: device.rtpCapabilities,
    });
    const consumed = await waitFor<{
      type: string;
      id: string;
      producerId: string;
      kind: 'audio' | 'video';
      rtpParameters: mstypes.RtpParameters;
      producerPaused?: boolean;
    }>(currentWs, (m) => m?.type === 'sfu-consumed' && m?.producerId === producerId);

    const consumer = await recvTransport.consume({
      id: consumed.id,
      producerId: consumed.producerId,
      kind: consumed.kind,
      rtpParameters: consumed.rtpParameters,
    });
    consumers.set(consumer.id, consumer);
    aggregateStream.addTrack(consumer.track);
    consumer.on('transportclose', () => {
      try { aggregateStream.removeTrack(consumer.track); } catch {}
      consumers.delete(consumer.id);
    });
    consumer.on('trackended', () => {
      try { aggregateStream.removeTrack(consumer.track); } catch {}
      consumers.delete(consumer.id);
    });

    wsSend(currentWs, { type: 'sfu-resume-consumer', consumerId: consumer.id });
    try { await consumer.resume(); } catch { /* ignore */ }

    if (!deliveredStream) {
      deliveredStream = true;
      opts.onStream(aggregateStream);
    }
  };

  /**
   * After an ingest producer closes, the broadcaster's camera producer
   * may still be live — re-consume anything we're missing.
   */
  const resyncMissingProducers = async (currentWs: WebSocket) => {
    if (!device || !recvTransport || stopped) return;
    wsSend(currentWs, { type: 'sfu-list-producers' });
    const list = await waitFor<{
      type: string;
      producers: { producerId: string; kind: string }[];
    }>(currentWs, (m) => m?.type === 'sfu-producers');
    for (const p of list.producers || []) {
      const has = Array.from(consumers.values()).some((c) => c.producerId === p.producerId);
      if (has) continue;
      const mediaKind: 'audio' | 'video' = p.kind === 'audio' ? 'audio' : 'video';
      try {
        await consumeProducer(currentWs, p.producerId, mediaKind);
      } catch (err) {
      }
    }
  };

  const setupViewer = async (currentWs: WebSocket) => {
    // The server replies to the viewer hello with a `welcome` that
    // carries the broadcaster's declared mode. Branch on it first
    // so we never spin up a recv transport for a 'signaling_only'
    // broadcast (no producers will ever exist; the transport's DTLS
    // would never converge and we'd fire ice_timeout in 30s for no
    // good reason).
    try {
      const welcome = await waitFor<{
        type: string;
        broadcasterMode?: 'media' | 'signaling_only';
      }>(currentWs, (m) => m?.type === 'welcome');
      if (welcome.broadcasterMode === 'signaling_only') {
        notifyMode('signaling_only');
        // Mark the watchdog satisfied — there is nothing to wait
        // for. The viewer's `live_agent_caption` listener (added
        // unconditionally in the main message handler below)
        // continues to fire as captions arrive.
        iceReached = true;
        emitDiag();
        return;
      }
      notifyMode('media');
    } catch {
      // No welcome ever arrived (ws closed early, or RPC timed out).
      // Fall through so the historical code path runs and the
      // existing error handling surfaces the failure.
    }

    wsSend(currentWs, { type: 'sfu-get-caps' });
    const caps = await waitFor<{ type: string; rtpCapabilities: mstypes.RtpCapabilities }>(
      currentWs, (m) => m?.type === 'sfu-router-caps');

    device = new Device();
    await device.load({ routerRtpCapabilities: caps.rtpCapabilities });

    wsSend(currentWs, { type: 'sfu-create-transport', direction: 'recv' });
    const tParams = await waitFor<{
      type: string; direction: 'send' | 'recv';
      id: string;
      iceParameters: mstypes.IceParameters;
      iceCandidates: mstypes.IceCandidate[];
      dtlsParameters: mstypes.DtlsParameters;
    }>(currentWs, (m) => m?.type === 'sfu-transport-created' && m?.direction === 'recv');

    try {
      for (const cand of tParams.iceCandidates || []) {
        const type = (cand as any)?.type;
        if (type === 'host' || type === 'srflx' || type === 'prflx' || type === 'relay') {
          remoteCandidateTypes.add(type);
        }
      }
    } catch { /* ignore */ }

    recvTransport = device.createRecvTransport({
      id: tParams.id,
      iceParameters: tParams.iceParameters,
      iceCandidates: tParams.iceCandidates,
      dtlsParameters: tParams.dtlsParameters,
      iceServers: getIceServers(),
    });

    recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      wsSend(currentWs, {
        type: 'sfu-connect-transport',
        transportId: recvTransport!.id,
        dtlsParameters,
      });
      waitFor(currentWs, (m) => m?.type === 'sfu-transport-connected' && m?.transportId === recvTransport!.id)
        .then(() => callback())
        .catch((err) => errback(err as Error));
    });

    recvTransport.on('connectionstatechange', (state) => {
      if (stopped) return;
      if (state === 'connected') iceReached = true;
      if (state === 'failed') {
        // Media path broke.  If the ws is still healthy this is a
        // genuine ICE failure — surface it.  If the ws itself
        // dropped, the ws-close path will drive reconnect instead.
        if (ws && ws.readyState === WebSocket.OPEN) {
          opts.onEnded?.('ice_failed');
        }
      }
      emitDiag();
    });

    recvTransport.observer.on('newproducer' as any, () => { /* informational */ });

    localCandidateTypes.add('relay');
    emitDiag();

    wsSend(currentWs, { type: 'sfu-list-producers' });
    const list = await waitFor<{
      type: string;
      producers: { producerId: string; kind: 'audio' | 'video' }[];
    }>(currentWs, (m) => m?.type === 'sfu-producers');

    for (const p of list.producers) {
      try { await consumeProducer(currentWs, p.producerId, p.kind); }
      catch (err) { try { console.warn('[sfu][viewer] consume failed', p, err); } catch {} }
    }
  };

  // Watchdog armed only on the first connect — the ICE path that
  // users care about is the initial one.  Subsequent reconnects
  // reuse the same `iceReached` sentinel; if they fail to
  // re-negotiate, the ws-close reconnect loop keeps trying within
  // `shouldResume`.
  //
  // For 'signaling_only' broadcasters, `iceReached` is flipped true
  // by `setupViewer` itself so this watchdog quietly no-ops — it
  // would otherwise fire ice_timeout 30s into every MCP-driven
  // session and dump the viewer back to the "Watch live" button.
  const iceWatchdog: ReturnType<typeof setTimeout> = setTimeout(() => {
    if (stopped || iceReached) return;
    if (broadcasterMode === 'signaling_only') return;
    opts.onEnded?.('ice_timeout');
  }, SFU_VIEWER_ICE_TIMEOUT_MS);

  const teardownWsState = () => {
    consumers.forEach((c) => { try { c.close(); } catch { /* ignore */ } });
    consumers = new Map();
    try { recvTransport?.close(); } catch { /* ignore */ }
    recvTransport = null;
    device = null;
    // Aggregate stream tracks will already have fired
    // `transportclose` when the transport was torn down; the
    // MediaStream stays attached to the UI <video> and will pick
    // up fresh tracks once setupViewer runs again.
  };

  const connect = () => {
    if (stopped) return;
    attempt += 1;
    const w = new WebSocket(url);
    ws = w;

    w.addEventListener('open', () => {
      if (stopped || ws !== w) return;
      emitDiag();
      wsSend(w, {
        type: 'hello', role: 'viewer', broadcasterId: opts.broadcasterId,
        wallet: opts.wallet, country: opts.country,
        // See webrtcBroadcastService.ts for the design rationale —
        // only emit the field when truthy to keep the wire format
        // backwards-compatible with older hubs.
        ...(opts.teleportAttached ? { teleportAttached: true } : {}),
        ...(opts.viewerToken ? { viewerToken: opts.viewerToken } : {}),
      });
      setupViewer(w).then(() => {
        if (stopped || ws !== w) return;
        const wasReconnect = connected;
        connected = true;
        if (wasReconnect) {
          opts.onReconnected?.();
        }
      }).catch((err) => {
        if (stopped || ws !== w) return;
        try { w.close(); } catch { /* ignore */ }
      });
    });

    w.addEventListener('message', async (ev) => {
      let msg: any;
      try { msg = JSON.parse(String(ev.data)); } catch { return; }
      if (!msg?.type) return;
      switch (msg.type) {
        case 'denied':
          stopped = true;
          opts.onDenied?.(msg.reason || 'denied');
          try { w.close(); } catch {}
          break;
        case 'session_ended':
          stopped = true;
          opts.onEnded?.(msg.reason || 'ended');
          try { w.close(); } catch {}
          break;
        case 'session_extended':
          if (typeof msg.scheduledEndAt === 'string') {
            opts.onSessionExtended?.({
              scheduledEndAt: msg.scheduledEndAt,
              extensionSeconds: msg.extensionSeconds,
              extensionCount: msg.extensionCount,
              fromViewer: msg.fromViewer,
            });
          }
          break;
        case 'viewers_update':
          if (typeof msg.count === 'number') opts.onViewersUpdate?.(msg.count);
          break;
        case 'left':
          if (msg.role === 'broadcaster') {
            // Server is going to follow up with `broadcaster_disconnected`
            // (grace window open) OR terminate the room.  We no longer
            // short-circuit with onEnded here — let `session_ended`
            // or the grace timeout drive the terminal path.  During
            // grace the viewer's ws stays open and we just surface
            // the disconnect indicator.
          }
          break;
        case 'broadcaster_disconnected':
          if (typeof msg.graceMs === 'number') {
            opts.onBroadcasterDisconnected?.(msg.graceMs);
          }
          break;
        case 'broadcaster_reconnected':
          opts.onBroadcasterReconnected?.();
          break;
        case 'broadcast_paused':
          opts.onBroadcastPaused?.();
          break;
        case 'broadcast_resumed':
          opts.onBroadcastResumed?.();
          break;
        case 'live_gift':
          try {
            opts.onGift?.({
              fromPeerId: String(msg.fromPeerId || ''),
              fromWallet: typeof msg.fromWallet === 'string' ? msg.fromWallet : null,
              assetCode: typeof msg.assetCode === 'string' ? msg.assetCode : 'XLM',
              assetIssuer: typeof msg.assetIssuer === 'string' ? msg.assetIssuer : null,
              amount: String(msg.amount || ''),
              memo: typeof msg.memo === 'string' ? msg.memo : '',
              txHash: typeof msg.txHash === 'string' ? msg.txHash : null,
              sentAt: typeof msg.sentAt === 'number' ? msg.sentAt : Date.now(),
            });
          } catch { /* ignore */ }
          break;
        case 'live_tool_event':
          try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const liveTools = require('./liveToolsService') as typeof import('./liveToolsService');
            const ev = {
              event: msg.event,
              sessionId: msg.sessionId,
              toolId: msg.toolId ?? null,
              tool: msg.tool ?? null,
              snapshot: msg.snapshot,
              error: msg.error,
            } as import('./liveToolsService').LiveToolEvent;
            (opts as any).onLiveToolEvent?.(ev);
            liveTools.dispatchLiveToolEvent(ev);
          } catch { /* ignore */ }
          break;
        case 'live_agent_caption':
        case 'spatial_object_caption':
          try {
            (opts as any).onAgentCaption?.(normaliseCaptionEvent(msg));
          } catch { /* ignore */ }
          break;
        case 'spatial_object_caption_cancel':
          try {
            (opts as any).onCaptionCancel?.({
              spatialObjectId: String(msg.spatial_object_id || ''),
              utteranceId: msg.utterance_id ? String(msg.utterance_id) : null,
              reason: String(msg.reason || 'cancelled'),
            });
          } catch { /* ignore */ }
          break;
        case 'sfu-new-producer':
          // Broadcaster added a new track — either initial (they
          // just enabled the mic) or post-reconnect (their
          // producers got re-created on the server).  Consume it
          // on the existing recv transport so the <video> picks
          // up the new track instantly.
          if (msg?.producerId && (msg.kind === 'audio' || msg.kind === 'video')) {
            try {
              // PlainTransport drone ingest is a second video producer.
              // HTMLMediaElement typically decodes the first video track only,
              // so drop the camera consumer first when ingest video lands.
              if (msg.ingest === true && msg.kind === 'video') {
                detachAllVideoConsumers();
              }
              await consumeProducer(w, msg.producerId, msg.kind);
            }
            catch (err) { try { console.warn('[sfu][viewer] new-producer consume failed', err); } catch { /* ignore */ } }
          }
          break;
        case 'sfu-producer-closed': {
          const pid = String(msg?.producerId || '');
          if (!pid) break;
          closeConsumersForProducer(pid);
          if (!stopped && ws === w) {
            try { await resyncMissingProducers(w); } catch (err) {
            }
          }
          break;
        }
        default: break;
      }
      try {
        const cand = msg?.candidate?.candidate;
        const kind = classifyCandidate(cand);
        if (kind) { remoteCandidateTypes.add(kind); emitDiag(); }
      } catch { /* ignore */ }
    });

    w.addEventListener('error', () => {
      if (stopped || ws !== w) return;
      emitDiag();
    });

    w.addEventListener('close', () => {
      if (ws !== w) return;
      ws = null;
      teardownWsState();
      emitDiag();

      if (stopped) return;

      const wantsReconnect = typeof opts.shouldResume === 'function'
        ? !!opts.shouldResume()
        : false;
      if (!wantsReconnect) {
        opts.onEnded?.('socket_closed');
        return;
      }
      const delay = reconnectDelayMs(attempt);
      opts.onReconnecting?.(attempt + 1);
      reconnectTimer = setTimeout(connect, delay);
    });
  };

  connect();

  return {
    broadcasterId: opts.broadcasterId,
    stop() {
      stopped = true;
      try { clearTimeout(iceWatchdog); } catch {}
      if (reconnectTimer) { try { clearTimeout(reconnectTimer); } catch {} reconnectTimer = null; }
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'leave' })); } catch { /* ignore */ }
      }
      teardownWsState();
      try { ws?.close(); } catch { /* ignore */ }
      ws = null;
    },
    sendGift(gift) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      try {
        ws.send(JSON.stringify({
          type: 'live_gift',
          assetCode: gift.assetCode,
          assetIssuer: gift.assetIssuer || null,
          amount: gift.amount,
          memo: gift.memo || '',
          txHash: gift.txHash || null,
        }));
        return true;
      } catch {
        return false;
      }
    },
  };
}

/**
 * Feature-flag helper.  Returns true when the frontend is configured
 * to route live-session media through the SFU.  Callers should fall
 * back to the P2P service when this returns false.
 */
export function isSfuEnabled(): boolean {
  const env = (typeof process !== 'undefined' && (process as any).env) || {};
  return String(env.REACT_APP_USE_SFU || '').toLowerCase() === 'true';
}
